import type { PropertyMetadata, StyleSDKClient } from './types';
import {
  isShorthand,
  getParentShorthands,
} from '../grouping/propertyRelationships';
import { getEquivalentFamilies } from '../grouping/propertyEquivalents';
import {
  decompose,
  decomposePropertiesWithDefinitionsAndNesting,
  AtomicValueWithNesting,
} from '../../../../apis/services/editActionsServices/nativeActions/globalDesign/common/decomposer';
import { compose } from '../../../../apis/services/editActionsServices/nativeActions/globalDesign/common/composer';
import { VariableResolver } from './variableResolver';
import pickBy from 'lodash/pickBy';
import uniq from 'lodash/uniq';

/**
 * Engine for CSS Custom Properties (CSS variables).
 *
 * Custom properties can be shorthands (e.g., --border-theme) or atomics (e.g., --color-primary).
 * Unlike cssProperties, we ALWAYS update custom properties directly (never via their atomics),
 * but we must decompose/compose shorthand values when updating specific atomics.
 */
export class CSSCustomPropertiesEngine {
  private readonly allowedProperties: Set<string>;

  constructor(
    allowedProperties: readonly string[],
    private sdkClient: StyleSDKClient,
    private themeVariables: Record<string, string> = {},
  ) {
    this.allowedProperties = new Set(allowedProperties);
  }

  /**
   * Build PropertyMetadata for CSS custom properties from manifest definitions.
   *
   * cssPropertyType = def.cssPropertyType (no fallbacks, matching SDK path).
   * isShorthand via propertyRelationships.isShorthand(cssPropertyType).
   */
  static buildMetadata(
    customPropsDefinitions: Record<string, any>,
  ): Record<string, PropertyMetadata> {
    const metadata: Record<string, PropertyMetadata> = {};

    for (const [key, def] of Object.entries(customPropsDefinitions)) {
      const cssPropertyType = def.cssPropertyType;
      if (!cssPropertyType) {
        continue;
      }
      metadata[key] = {
        key,
        isShorthand: isShorthand(cssPropertyType), // Use cssPropertyType to determine shorthand
        namespace: 'cssCustomProperties',
        cssPropertyType,
      };
    }

    return metadata;
  }

  /**
   * Discover custom properties from manifest that match allowed list.
   */
  async discoverProperties(): Promise<Record<string, PropertyMetadata>> {
    const definitions = await this.sdkClient.getStyleDefinitions();
    const customProps = definitions.cssCustomProperties || {};

    const allMetadata = CSSCustomPropertiesEngine.buildMetadata(customProps);

    // Filter by allowedProperties
    return pickBy(allMetadata, (meta) =>
      this.allowedProperties.has(meta.cssPropertyType),
    );
  }

  /**
   * Get all custom property values (decompose shorthands recursively).
   * Decomposed keys are CSS property types, not custom property IDs.
   * Includes eligibility information - properties defined in metadata but without values
   * will appear in decomposed result with empty arrays.
   */
  async getAllValues(metadata: Record<string, PropertyMetadata>): Promise<{
    raw: Record<string, string>;
    decomposed: Record<string, AtomicValueWithNesting[]>;
  }> {
    const styles = await this.sdkClient.getStyles();
    const customProps = styles.cssCustomProperties || {};
    const raw: Record<string, string> = {};

    // Filter to only discovered properties with values
    const customPropsWithValues: Record<string, string> = {};
    for (const key of Object.keys(metadata)) {
      if (customProps[key] !== undefined) {
        raw[key] = customProps[key];
        customPropsWithValues[key] = customProps[key];
      }
    }

    // Collect unique cssPropertyTypes as definitions
    const definitions = Array.from(
      new Set(Object.values(metadata).map((meta) => meta.cssPropertyType)),
    );

    // Use enhanced function with type mapper and nesting level tracking
    const decomposed = decomposePropertiesWithDefinitionsAndNesting(
      definitions,
      customPropsWithValues,
      (key) => metadata[key].cssPropertyType, // Type mapper for custom properties
    );

    return {
      raw,
      decomposed,
    };
  }

  /**
   * Update custom properties with atomic normalization and CSS variable handling.
   * Returns the updates without applying them (coordinator will apply).
   *
   * Algorithm:
   * 1. Separate variable changes from regular changes
   * 2. For variable shorthands: check if there are atomic overrides from same family
   *    - If no atomic overrides: set variable directly (skip decomposition)
   *    - If atomic overrides exist: resolve variable, merge with atomics, recompose
   * 3. Decompose regular changes and resolved variables to atomics
   * 4. Decompose current values (skip families replaced by pure variables)
   * 5. Merge atomics (changes override current)
   * 6. Recompose each manifest property
   *
   * @param changes - Changes keyed by cssPropertyType
   * @param metadata - Property metadata keyed by manifest property ID
   */
  async updateProperties(
    changes: Record<string, string>,
    metadata: Record<string, PropertyMetadata>,
  ): Promise<Record<string, string>> {
    const currentStyles = await this.sdkClient.getStyles();
    const currentCustomProps = currentStyles.cssCustomProperties || {};

    // ═══════════════════════════════════════════════════════════════
    // STEP 1: Separate variable changes from regular changes
    // ═══════════════════════════════════════════════════════════════

    const variableShorthands: Record<string, string> = {}; // cssPropertyType -> var value
    const atomicVariables: Record<string, string> = {}; // cssPropertyType -> var value
    const regularChanges: Record<string, string> = {}; // cssPropertyType -> value

    for (const [changeKey, changeValue] of Object.entries(changes)) {
      const isVariable = this.containsVariable(changeValue);
      const isShorthandKey = isShorthand(changeKey);

      if (isVariable && isShorthandKey) {
        variableShorthands[changeKey] = changeValue;
      } else if (isVariable) {
        atomicVariables[changeKey] = changeValue;
      } else {
        regularChanges[changeKey] = changeValue;
      }
    }

    // ═══════════════════════════════════════════════════════════════
    // STEP 2: For each variable shorthand, check if there's an atomic override
    // ═══════════════════════════════════════════════════════════════

    const pureVariableShorthands: Record<string, string> = {}; // cssPropertyType -> var value (set directly)
    const variablesToResolve: Record<string, string> = {}; // cssPropertyType -> var value (need resolution)

    const regularChangesFamilies = this.getFamilies(
      Object.keys(regularChanges),
    );

    // cssPropertyTypes backed by an actual manifest custom property.
    // A variable shorthand maps directly onto its own property only when that
    // property is defined; otherwise it must be distributed across the atomic
    // sides that *are* defined (e.g. directional --border-*-color).
    const manifestPropertyTypes = new Set(
      Object.values(metadata)
        .filter((meta) => !meta.isExpandedAtomic)
        .map((meta) => meta.cssPropertyType),
    );

    for (const [changeKey, varValue] of Object.entries(variableShorthands)) {
      const family = this.getShorthandFamily(changeKey);

      // Conflict: variable shorthand + regular atomic override in the same
      // family → resolve the variable and merge with the atomic change.
      if (regularChangesFamilies.has(family)) {
        variablesToResolve[changeKey] = varValue;
        continue;
      }

      // Distribute the var() token across the atomic sides, mirroring how a
      // literal shorthand is decomposed in STEP 3 but keeping the variable
      // reference intact. Distributed atomics flow through atomicVariables, so
      // they are handled by Case 2 (and by enrichWithEquivalentAtomics for
      // logical/physical equivalents). Sides without a manifest definition are
      // never emitted in STEP 6, so distributing unconditionally is safe.
      const decomposed = decompose(changeKey, varValue);
      const atomicEntries = decomposed
        ? Object.entries(decomposed).filter(
            (entry): entry is [string, string] => entry[1] !== null,
          )
        : [];

      atomicEntries.forEach(([atomicType, atomicValue]) => {
        // Explicit atomic variables from the same change set always win.
        if (!(atomicType in atomicVariables)) {
          this.decomposeToLowestAtomics(
            atomicType,
            atomicValue,
            atomicVariables,
          );
        }
      });

      // Also set the variable directly on the shorthand custom property when it
      // is defined, so the shorthand and its per-side values stay consistent
      // (matching how a literal shorthand updates both). When the shorthand is
      // not decomposable at all, this direct write is the only resulting update.
      if (manifestPropertyTypes.has(changeKey) || atomicEntries.length === 0) {
        pureVariableShorthands[changeKey] = varValue;
      }
    }

    // ═══════════════════════════════════════════════════════════════
    // STEP 3: Build atomics from resolved variables + regular changes
    // Order matters: resolved variables first, then explicit changes override
    // ═══════════════════════════════════════════════════════════════

    const changeAtomics: Record<string, string> = {};

    // 1. Resolved variable shorthands first (base values)
    for (const [key, varValue] of Object.entries(variablesToResolve)) {
      const resolvedValue = VariableResolver.resolveVariableRecursively(
        varValue,
        this.themeVariables,
      );
      this.decomposeToLowestAtomics(key, resolvedValue, changeAtomics);
    }

    // 2. Regular changes override resolved variable atomics
    for (const [key, value] of Object.entries(regularChanges)) {
      this.decomposeToLowestAtomics(key, value, changeAtomics);
    }

    // 3. Atomic variable changes override everything (they're already atomic)
    for (const [changeKey, varValue] of Object.entries(atomicVariables)) {
      changeAtomics[changeKey] = varValue;
    }

    // 4. Normalize logical/physical equivalent families so metadata-backed targets
    // can still receive complete directional updates.
    const enrichedChangeAtomics = this.enrichWithEquivalentAtomics(
      changeAtomics,
      metadata,
    );

    // ═══════════════════════════════════════════════════════════════
    // STEP 4: Decompose current values
    // SKIP families with pure variable shorthands (will be replaced entirely)
    // ═══════════════════════════════════════════════════════════════

    const familiesReplacedByVariable = this.getFamilies(
      Object.keys(pureVariableShorthands),
    );
    const atomicChangeFamilies = this.getFamilies(
      Object.keys(enrichedChangeAtomics),
    );

    const currentAtomics: Record<string, string> = {};

    for (const [manifestKey, meta] of Object.entries(metadata)) {
      if (meta.isExpandedAtomic) {
        continue;
      }

      const family = this.getShorthandFamily(meta.cssPropertyType);

      // Skip families entirely replaced by a variable
      if (familiesReplacedByVariable.has(family)) {
        continue;
      }

      const currentValue = currentCustomProps[manifestKey];
      if (!currentValue) {
        continue;
      }

      if (
        this.containsVariable(currentValue) &&
        !atomicChangeFamilies.has(family)
      ) {
        continue;
      }

      const comparableValue = this.getComparableValue(currentValue);
      if (!comparableValue) {
        continue;
      }

      this.decomposeToLowestAtomics(
        meta.cssPropertyType,
        comparableValue,
        currentAtomics,
      );
    }

    // ═══════════════════════════════════════════════════════════════
    // STEP 5: Merge atomics (changes override current)
    // ═══════════════════════════════════════════════════════════════

    const finalAtomics = { ...currentAtomics };

    for (const [atomicType, nextValue] of Object.entries(
      enrichedChangeAtomics,
    )) {
      const currentAtomicValue = currentAtomics[atomicType];

      if (
        currentAtomicValue !== undefined &&
        !this.containsVariable(nextValue) &&
        !this.hasMeaningfulAtomicDifference(
          { [atomicType]: nextValue },
          { [atomicType]: currentAtomicValue },
        )
      ) {
        continue;
      }

      finalAtomics[atomicType] = nextValue;
    }

    // ═══════════════════════════════════════════════════════════════
    // STEP 6: Build final updates for each manifest property
    // ═══════════════════════════════════════════════════════════════

    const updates: Record<string, string> = {};

    for (const [manifestKey, meta] of Object.entries(metadata)) {
      if (meta.isExpandedAtomic) {
        continue;
      }

      let newValue: string | null = null;
      let comparisonMode: 'semantic' | 'raw' = 'semantic';
      const hasAtomicVariableChange = Array.from(
        this.getAtomicTypesForProperty(meta.cssPropertyType),
      ).some((atomicType) => atomicVariables[atomicType] !== undefined);

      // Case 1: Pure variable shorthand for this exact cssPropertyType
      if (pureVariableShorthands[meta.cssPropertyType]) {
        newValue = pureVariableShorthands[meta.cssPropertyType];
        comparisonMode = 'raw';
      }
      // Case 2: Atomic variable change
      else if (!meta.isShorthand && atomicVariables[meta.cssPropertyType]) {
        newValue = atomicVariables[meta.cssPropertyType];
        comparisonMode = 'raw';
      }
      // Case 3: Direct atomic change (non-variable)
      else if (!meta.isShorthand && regularChanges[meta.cssPropertyType]) {
        newValue = regularChanges[meta.cssPropertyType];
      }
      // Case 4: Shorthand - recompose from merged atomics
      else if (meta.isShorthand) {
        newValue = this.composeRecursive(meta.cssPropertyType, finalAtomics);
        if (newValue) {
          if (hasAtomicVariableChange) {
            comparisonMode = 'raw';
          }
        } else if (variablesToResolve[meta.cssPropertyType]) {
          newValue = variablesToResolve[meta.cssPropertyType];
          comparisonMode = 'raw';
        }
      }
      // Case 5: Atomic from merged atomics
      else {
        newValue = finalAtomics[meta.cssPropertyType] ?? null;
        if (newValue && hasAtomicVariableChange) {
          comparisonMode = 'raw';
        }
      }

      if (!newValue) {
        continue;
      }

      if (
        this.shouldUpdatePropertyValue(
          meta.cssPropertyType,
          newValue,
          currentCustomProps[manifestKey],
          comparisonMode,
        )
      ) {
        updates[manifestKey] = newValue;
      }
    }

    return updates;
  }

  /**
   * Recursively decompose a property to its lowest atomic level.
   * Results are added to the provided atomics object.
   */
  private decomposeToLowestAtomics(
    propertyType: string,
    value: string,
    atomics: Record<string, string>,
  ): void {
    if (!isShorthand(propertyType)) {
      // Already atomic - store directly
      atomics[propertyType] = value;
      return;
    }

    // Decompose shorthand
    const decomposed = decompose(propertyType, value);
    if (
      !decomposed ||
      Object.values(decomposed).every((atomValue) => atomValue === null)
    ) {
      // Decomposition failed - treat as atomic
      atomics[propertyType] = value;
      return;
    }

    // Recursively decompose any shorthand values in the result
    for (const [key, atomValue] of Object.entries(decomposed)) {
      if (atomValue !== null) {
        this.decomposeToLowestAtomics(key, atomValue, atomics);
      }
    }
  }

  /**
   * Enriches incoming atomic changes across logical/physical equivalent families before the engine
   * tries to match them to the target metadata.
   *
   * Why this exists:
   * The incoming change payload may contain logical border keys such as `borderInlineStartStyle`
   * and `borderInlineEndStyle`, while the current target metadata only exposes physical keys such as
   * `borderLeftStyle` and `borderRightStyle`, or a shorthand like `border`. In that case the raw
   * logical keys are not directly representable by the target engine call. If we forward them as-is,
   * the custom-properties engine can only see a partial family and may silently miss left/right
   * updates or treat the change as a no-op.
   *
   * What this method does:
   * 1. Builds the set of atomic property types that the current target metadata can actually represent.
   * 2. Groups changed atomics by their equivalence family using PROPERTY_EQUIVALENTS_MAP.
   * 3. Looks for families where at least one changed key is not representable by the current target.
   * 4. Only when multiple changed keys from that family all share the same value, it backfills the
   *    missing representable family members with that shared value.
   * 5. It never overwrites keys that were already explicitly present in the incoming changes.
   *
   * Why the rules are strict:
   * - A single logical key like `borderInlineStartStyle: 'dashed'` is ambiguous for a target that only
   *   knows physical keys, so we leave it unchanged rather than guessing.
   * - Mixed values inside the same family are also left unchanged, because copying one value to all
   *   representable siblings would be wrong.
   *
   * Example: target metadata can represent
   * { borderTopStyle, borderRightStyle, borderBottomStyle, borderLeftStyle }
   *
   * Incoming atomics:
   * {
   *   borderTopStyle: 'dashed',
   *   borderBottomStyle: 'dashed',
   *   borderInlineStartStyle: 'dashed',
   *   borderInlineEndStyle: 'dashed',
   * }
   *
   * Enriched result:
   * {
   *   borderTopStyle: 'dashed',
   *   borderRightStyle: 'dashed',
   *   borderBottomStyle: 'dashed',
   *   borderLeftStyle: 'dashed',
   *   borderInlineStartStyle: 'dashed',
   *   borderInlineEndStyle: 'dashed',
   * }
   *
   * This lets downstream composition/update logic rebuild a full physical border family instead of
   * dropping the logical-only parts.
   *
   * Counter-example:
   * { borderInlineStartStyle: 'dashed' }
   * - Returns unchanged, because one isolated logical key is not enough to safely infer the missing
   *   representable siblings.
   */
  private enrichWithEquivalentAtomics(
    changeAtomics: Record<string, string>,
    metadata: Record<string, PropertyMetadata>,
  ): Record<string, string> {
    const eligibleAtomics = this.getEligibleAtomicTypes(metadata);
    const enrichedAtomics = { ...changeAtomics };

    const equivalentPropertyFamilies = getEquivalentFamilies(
      Object.keys(changeAtomics),
    );

    for (const family of equivalentPropertyFamilies) {
      if (
        !this.hasMissingAtomics(family.observedPropertyTypes, eligibleAtomics)
      ) {
        continue;
      }

      const commonValue = this.getCommonAtomicsValue(
        family.observedPropertyTypes,
        changeAtomics,
      );
      if (commonValue === undefined) {
        continue;
      }

      this.addMissingAtomics(
        enrichedAtomics,
        family.familyPropertyTypes,
        eligibleAtomics,
        commonValue,
      );
    }

    return enrichedAtomics;
  }

  private hasMissingAtomics(
    observedPropertyTypes: string[],
    eligibleAtomics: Set<string>,
  ): boolean {
    return (
      observedPropertyTypes.length > 1 &&
      observedPropertyTypes.some(
        (propertyType) => !eligibleAtomics.has(propertyType),
      )
    );
  }

  private getCommonAtomicsValue(
    observedPropertyTypes: string[],
    changeAtomics: Record<string, string>,
  ): string | undefined {
    const sharedValues = uniq(
      observedPropertyTypes.map((propertyType) => changeAtomics[propertyType]),
    );

    return sharedValues.length === 1 ? sharedValues[0] : undefined;
  }

  private addMissingAtomics(
    enrichedAtomics: Record<string, string>,
    familyPropertyTypes: Set<string>,
    eligibleAtomics: Set<string>,
    commonValue: string,
  ): void {
    for (const propertyType of familyPropertyTypes) {
      if (
        eligibleAtomics.has(propertyType) &&
        enrichedAtomics[propertyType] === undefined
      ) {
        enrichedAtomics[propertyType] = commonValue;
      }
    }
  }

  private getEligibleAtomicTypes(
    metadata: Record<string, PropertyMetadata>,
  ): Set<string> {
    const allAtomicTypes = Object.values(metadata)
      .filter((meta) => !meta.isExpandedAtomic)
      .flatMap((meta) =>
        Array.from(this.getAtomicTypesForProperty(meta.cssPropertyType)),
      );

    const eligibleAtomics = new Set(allAtomicTypes);

    return eligibleAtomics;
  }

  private collectEligibleAtomicTypes(
    propertyType: string,
    eligibleAtomics: Set<string>,
  ): void {
    if (!isShorthand(propertyType)) {
      eligibleAtomics.add(propertyType);
      return;
    }

    const decomposed = decompose(propertyType, 'dummy');
    if (!decomposed) {
      eligibleAtomics.add(propertyType);
      return;
    }

    Object.keys(decomposed).forEach((key) =>
      this.collectEligibleAtomicTypes(key, eligibleAtomics),
    );
  }

  private getAtomicTypesForProperty(propertyType: string): Set<string> {
    const atomicTypes = new Set<string>();
    this.collectEligibleAtomicTypes(propertyType, atomicTypes);
    return atomicTypes;
  }

  /**
   * Decides whether a candidate should be written back for a manifest property.
   *
   * Semantic mode resolves variables and normalizes formatting noise before comparing,
   * so equivalent values like `#000` and `#000000` or resolved shorthand payloads stay
   * a no-op. Raw mode compares the original strings directly so explicit variable-token
   * replacements still apply even when both tokens resolve to the same value.
   */
  private shouldUpdatePropertyValue(
    propertyType: string,
    nextValue: string,
    currentValue: string | undefined,
    comparisonMode: 'semantic' | 'raw',
  ): boolean {
    if (comparisonMode === 'raw') {
      return nextValue !== currentValue;
    }

    const candidate = this.getComparableAtomics(propertyType, nextValue);
    const current = this.getComparableAtomics(propertyType, currentValue);

    return this.hasMeaningfulAtomicDifference(candidate, current);
  }

  /**
   * Converts a raw property value into the atomic shape used for semantic comparison.
   *
   * The method first resolves the value through getComparableValue(), then decomposes
   * the result down to the lowest atomic properties. This lets shorthand values and
   * variable-backed values be compared using the same atomic representation.
   *
   * Example: getComparableAtomics('border', 'var(--theme-border)')
   * - Engine theme variables: { 'theme-border': '1px solid #000' }
   * - Resolves to: '1px solid #000'
   * - Returns: { borderWidth: '1px', borderStyle: 'solid', borderColor: '#000' }
   */
  private getComparableAtomics(
    propertyType: string,
    value: string | undefined,
  ): Record<string, string> {
    const comparableValue = this.getComparableValue(value);
    if (!comparableValue) {
      return {};
    }

    const atomics: Record<string, string> = {};

    this.decomposeToLowestAtomics(propertyType, comparableValue, atomics);

    return atomics;
  }

  /**
   * Checks whether two atomic maps differ in a way that should produce a real update.
   *
   * Comparison happens on the union of keys from both inputs, and each side is normalized
   * first so formatting-only noise does not count as a change.
   *
   * Example: { borderColor: '#000' } vs { borderColor: '#000000' }
   * - normalizeValue() makes both sides '#000000'
   * - Returns: false
   *
   * Example: { borderStyle: 'double' } vs { borderStyle: 'solid' }
   * - Returns: true
   */
  private hasMeaningfulAtomicDifference(
    candidateAtomics: Record<string, string>,
    currentAtomics: Record<string, string>,
  ): boolean {
    const atomicKeys = new Set([
      ...Object.keys(candidateAtomics),
      ...Object.keys(currentAtomics),
    ]);

    return Array.from(atomicKeys).some(
      (atomicType) =>
        this.normalizeValue(candidateAtomics[atomicType]) !==
        this.normalizeValue(currentAtomics[atomicType]),
    );
  }

  /**
   * Recursively compose a property from atomics.
   * If intermediate shorthand values are missing, tries to compose them first.
   *
   * Example: compose('border', atomics)
   * - Needs: borderWidth, borderStyle, borderColor
   * - If borderColor is missing, tries to compose it from borderTopColor, borderRightColor, etc.
   */
  private composeRecursive(
    propertyType: string,
    atomics: Record<string, string>,
  ): string | null {
    // Try direct composition first
    const directCompose = compose(propertyType, atomics);
    if (directCompose) {
      return directCompose;
    }

    // If direct composition failed, try to build missing intermediate shorthands
    // Get what atomics this shorthand needs (1-level children)
    const oneLevel = decompose(propertyType, 'dummy');
    if (!oneLevel) {
      // Not a known shorthand, can't compose
      return null;
    }

    // Build intermediate values by recursively composing them
    const enrichedAtomics: Record<string, string> = { ...atomics };
    for (const intermediateKey of Object.keys(oneLevel)) {
      if (!atomics[intermediateKey] && isShorthand(intermediateKey)) {
        // Missing intermediate shorthand - try to compose it recursively
        const composed = this.composeRecursive(intermediateKey, atomics);
        if (composed) {
          enrichedAtomics[intermediateKey] = composed;
        }
      }
    }

    // Try composition again with enriched atomics
    return compose(propertyType, enrichedAtomics);
  }

  /**
   * For custom properties, setPropertiesDirectly applies changes without change detection.
   * Unlike updateProperties, this will always return the changes even if values haven't changed.
   * Returns the updates without applying them (coordinator will apply).
   */
  async setPropertiesDirectly(
    changes: Record<string, string>,
    metadata: Record<string, PropertyMetadata>,
  ): Promise<Record<string, string>> {
    const updates: Record<string, string> = {};

    // Get manifest properties (not expanded atomics)
    const manifestProps = Object.keys(metadata).filter(
      (key) => !metadata[key].isExpandedAtomic,
    );

    // For setPropertiesDirectly, just pass through changes for properties in manifest
    for (const [changeKey, changeValue] of Object.entries(changes)) {
      if (manifestProps.includes(changeKey)) {
        updates[changeKey] = changeValue;
      }
    }

    return updates;
  }

  /**
   * Checks if a value contains a CSS variable reference.
   */
  private containsVariable(value: string): boolean {
    if (!value) {
      return false;
    }
    return value.includes('var(');
  }

  /**
   * Resolves a value into the semantic form that should be compared during changes detection.
   *
   * Literal values are returned as-is. Variable-backed values are resolved recursively until
   * they either reach a concrete semantic value or can no longer be resolved.
   *
   * Example: getComparableValue('var(--theme-border)')
   * - Engine theme variables: { 'theme-border': '1px solid #000' }
   * - Returns: '1px solid #000'
   *
   * Example: getComparableValue('var(--missing-color)')
   * - Engine theme variables: {}
   * - Returns: 'var(--missing-color)'
   *
   * Example: getComparableValue('12px')
   * - Returns: '12px'
   */
  private getComparableValue(value: string | undefined): string | undefined {
    if (!value) {
      return undefined;
    }

    // Meaningful-delta checks compare resolved semantics, not raw variable tokens.
    return this.containsVariable(value)
      ? VariableResolver.resolveVariables(value, this.themeVariables)
      : value;
  }

  /**
   * Normalizes a scalar value before equality comparison.
   *
   * This removes formatting-only differences that should not trigger an update:
   * trimmed whitespace, accidental 'spx' suffixes, and 3-digit / uppercase hex colors.
   *
   * Example: normalizeValue('  12spx  ')
   * - Returns: '12px'
   *
   * Example: normalizeValue('#ABC')
   * - Returns: '#aabbcc'
   *
   * Example: normalizeValue(undefined)
   * - Returns: ''
   */
  private normalizeValue(value: string | undefined): string {
    if (!value) {
      return '';
    }

    return value
      .trim()
      .replace(/spx\b/gi, 'px')
      .replace(/#([0-9a-f]{3}|[0-9a-f]{6})\b/gi, (_match, hex) => {
        const normalizedHex = hex.toLowerCase();

        if (normalizedHex.length === 3) {
          return `#${normalizedHex
            .split('')
            .map((char: string) => `${char}${char}`)
            .join('')}`;
        }

        return `#${normalizedHex}`;
      });
  }

  /**
   * Gets the root shorthand family for a property.
   * For example: fontSize -> font, borderTopWidth -> border
   */
  private getShorthandFamily(propertyType: string): string {
    const parents = getParentShorthands(propertyType);
    return parents.length > 0 ? parents[parents.length - 1] : propertyType;
  }

  private getFamilies(propertyTypes: Iterable<string>): Set<string> {
    return new Set(
      Array.from(propertyTypes, (propertyType) =>
        this.getShorthandFamily(propertyType),
      ),
    );
  }
}
